// ==========================================
// PlatformFeedbackModel.js
// ==========================================
export class PlatformFeedbackModel {
  static ALLOWED_KEYS = Object.freeze([
    "id",
    "feedbackId",
    "userId",
    "userName",
    "userEmail",
    "category",
    "rating",
    "comment",
    "status",
    "companyId",
    "company",
    "metadata",
  ]);

  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "deletedFlag",
  ]);

  static ID_FIELD   = "feedbackId";
  static NAME_FIELD = "category";

  static CATEGORY_VALUES = Object.freeze([
    "general",
    "ui-ux",
    "performance",
    "features",
    "support",
    "bug",
  ]);

  static STATUS_VALUES = Object.freeze([
    "active",
    "reviewed",
    "resolved",
    "archived",
  ]);

  constructor(data = {}, { isCreation = false } = {}) {
    for (const key of PlatformFeedbackModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        this.metadata = {
          createdAt: null,
          createdBy: null,
          updatedAt: null,
          updatedBy: null,
          deletedFlag: false,
        };
      } else if (key === "status") {
        this.status = "active";
      } else if (key === "rating") {
        this.rating = 0;
      } else if (key === "category") {
        this.category = "general";
      } else {
        this[key] = null;
      }
    }

    if (isCreation) {
      this.id = data.id || crypto.randomUUID();
      this.feedbackId = data.feedbackId || this.id;
      this.userId = data.userId || null;
      this.userName = data.userName || null;
      this.userEmail = data.userEmail || data.email || null;
      this.category = data.category || "general";
      this.rating = data.rating || 0;
      this.comment = data.comment || "";
      this.status = data.status || "active";
      this.companyId = data.companyId || null;
      this.company = data.company || null;

      const now = new Date().toISOString();
      this.metadata = {
        createdAt: data.metadata?.createdAt || data.timestamp || data.createdAt || now,
        createdBy: data.metadata?.createdBy || data.createdBy || data.userEmail || "system",
        updatedAt: data.metadata?.updatedAt || data.updatedAt || now,
        updatedBy: data.metadata?.updatedBy || data.updatedBy || data.userEmail || "system",
        deletedFlag: data.metadata?.deletedFlag ?? false,
      };
    } else {
      for (const key of Object.keys(data)) {
        if (!PlatformFeedbackModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          this.metadata = { ...this.metadata, ...(data.metadata || {}) };
        } else {
          this[key] = data[key];
        }
      }
      if (data.createdAt) this.metadata.createdAt = data.createdAt;
      if (data.createdBy) this.metadata.createdBy = data.createdBy;
      if (data.updatedAt) this.metadata.updatedAt = data.updatedAt;
      if (data.updatedBy) this.metadata.updatedBy = data.updatedBy;
    }
  }

  isValid() {
    if (!this.userId || !String(this.userId).trim()) return false;
    if (!this.userEmail || !String(this.userEmail).trim()) return false;
    if (this.rating == null) return false;
    const r = Number(this.rating);
    if (!Number.isInteger(r) || r < 1 || r > 5) return false;
    if (
      this.category &&
      !PlatformFeedbackModel.CATEGORY_VALUES.includes(this.category)
    ) {
      return false;
    }
    if (
      this.status &&
      !PlatformFeedbackModel.STATUS_VALUES.includes(this.status)
    ) {
      return false;
    }
    return true;
  }

  toPayload() {
    const payload = {};
    for (const key of PlatformFeedbackModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        payload.metadata = {};
        for (const mk of PlatformFeedbackModel.ALLOWED_METADATA_KEYS) {
          payload.metadata[mk] =
            this.metadata?.[mk] ?? (mk === "deletedFlag" ? false : null);
        }
      } else if (this[key] !== undefined) {
        payload[key] = this[key];
      }
    }
    // Keep legacy top-level field the UI relies on for sorting.
    payload.timestamp = this.metadata?.createdAt || null;
    return payload;
  }

  toJSON() {
    return this.toPayload();
  }
}